import React, { useEffect, useRef, useState } from 'react';
import SceneHeader from './SceneHeader';
import { Award, Users, Globe, Rocket } from 'lucide-react';

const iconMap = { Award, Users, Globe, Rocket };

const useCountUp = (end, duration = 2200, trigger = false) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    const startTs = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(end * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, trigger]);
  return n;
};

const BigStat = ({ value, label, inView, delay }) => {
  const match = /^(\d+)([^\d]*)$/.exec(value);
  const canAnim = !!match;
  const numeric = canAnim ? parseInt(match[1], 10) : 0;
  const suffix = canAnim ? match[2] : '';
  const count = useCountUp(numeric, 2400, inView && canAnim);
  return (
    <div className="relative border-t border-white/10 pt-6 animate-fadeInUp" style={{ animationDelay: `${delay}ms` }}>
      <div className="font-light text-6xl md:text-7xl lg:text-8xl chromatic-text tracking-[-0.04em] tabular-nums leading-none">
        {canAnim ? `${count}${suffix}` : value}
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
    </div>
  );
};

const highlights = [
  { icon: 'Award',  title: 'Industry leader',  text: 'Recognised as a top innovator in applied AI and real-time systems.' },
  { icon: 'Users',  title: 'Expert team',      text: '20+ engineers, designers and research leads across 4 time zones.' },
  { icon: 'Globe',  title: 'Global reach',     text: 'Serving teams across 5+ countries with always-on engineering.' },
  { icon: 'Rocket', title: 'Innovation first', text: 'We ship weekly. The future is a delivery schedule, not a slogan.' },
];

const About = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    obs.observe(el);

    const obs2 = new IntersectionObserver((entries) => {
      entries.forEach((x) => { if (x.isIntersecting) x.target.classList.add('in-view'); });
    }, { threshold: 0.1 });
    el.querySelectorAll('.reveal, .reveal-stagger').forEach((n) => obs2.observe(n));

    return () => { obs.disconnect(); obs2.disconnect(); };
  }, []);

  return (
    <section id="about" ref={ref} className="relative py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SceneHeader
          chapter="03 / 06"
          subtitle="Studio"
          title={<>A quiet studio for <span className="font-serif neon-text">loud</span> ambitions.</>}
          description="We are a tight, senior team from AI research, game engines, fintech and design. We take fewer projects, and go deeper."
        />

        {/* Big stats row */}
        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8">
          <BigStat value="15+"  label="Years in the craft" inView={inView} delay={100} />
          <BigStat value="20+"  label="Specialists"        inView={inView} delay={200} />
          <BigStat value="10+"  label="Active partners"    inView={inView} delay={300} />
          <BigStat value="5+"   label="Countries"          inView={inView} delay={400} />
        </div>

        {/* Highlights as cinematic rows */}
        <div className="mt-32 grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4 md:sticky md:top-28 reveal">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">What we stand for</div>
            <h3 className="mt-4 font-light text-3xl md:text-4xl chromatic-text leading-[1.05] tracking-[-0.02em]">
              Four principles we ship by.
            </h3>
            <p className="mt-4 text-sm text-white/55 leading-relaxed">
              No theatrics. No vanity metrics. Just the quiet, compounding work of a studio that refuses to ship anything it wouldn&apos;t use itself.
            </p>
          </div>

          <ul className="md:col-span-8 reveal-stagger">
            {highlights.map((h, i) => {
              const Icon = iconMap[h.icon];
              return (
                <li
                  key={h.title}
                  className="group flex items-start gap-6 md:gap-10 py-8 border-t border-white/10 last:border-b first:border-t-0 md:first:border-t transition-colors duration-500 hover:border-cyan-400/30"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 mt-2 shrink-0 w-10">
                    0{i + 1}
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-cyan-400/40 transition-colors duration-500">
                    <Icon className="w-5 h-5 text-cyan-300" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-light text-2xl md:text-3xl chromatic-text tracking-[-0.01em]">{h.title}</h4>
                    <p className="mt-2 text-sm text-white/55 leading-relaxed max-w-xl">{h.text}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default About;
